import { styled } from '@mui/system'
import { uniqueId, noop } from 'es-toolkit/compat'
import useSplitMap from 'hooks/use-split-map'
import { defaultBackground, defaultShadow } from 'components/foundation'
import React, { useEffect, useState } from 'react'

/**
 * Create a Map.
 *
 * @param {object}   props
 * @param {string}   props.id      An identifier for the map. If none is provided one will get genereted. In either case
 *                                 the id will be available on props.onReady function.
 * @param {function} props.onReady When component is rendered and ready this function will run with the map's id as an argument.
 */
const FarmtrxMapB = React.forwardRef(
  ({ id: idProp, onReady = noop, children }, ref) => {
    const [id, setId] = useState(idProp)

    const { map_b } = useSplitMap()

    useEffect(() => {
      if (map_b) {
        map_b.setTarget(ref.current)
        setId(uniqueId('map-'))
      }
    }, [map_b])

    useEffect(() => {
      if (id) {
        onReady(id)
      }
    }, [id])

    return (
      <StyledWrapper>
        <StyledMap id={id} ref={ref}>
          {children}
        </StyledMap>
      </StyledWrapper>
    )
  },
)

FarmtrxMapB.displayName = 'OpenLayersMap'

export default FarmtrxMapB

const MAP_INSET = '4px'

const StyledWrapper = styled('div')({
  flex: 1,
  padding: MAP_INSET,
  width: '100%',
  borderRadius: '4px',
  position: 'relative',
  ...defaultBackground,
  ...defaultShadow,
})

const StyledMap = styled('div')({
  height: '100%',
  width: '100%',
  position: 'relative',
})

// Overlays sit outside the OL target, clear of the DOM OpenLayers manages.
// Inset matches StyledWrapper's padding so overlay offsets and percentages
// still resolve against the map area.
const StyledOverlay = styled('div')({
  position: 'absolute',
  inset: MAP_INSET,
  overflow: 'hidden',
  pointerEvents: 'none',
  '& > *': {
    pointerEvents: 'auto',
  },
})
